import { z } from 'zod';
import { containsAdviceWording } from '../schemas.js';
import { assumptionEvidenceSchema, assumptionSetContentSchema } from './content.js';
import { assumptionValueSchema } from './value.js';
/**
 * API contracts for versioned firm assumption sets (spec §10.4).
 *
 * Per-resource: a request names ONE set or ONE version and carries the revision
 * it was based on. Nothing here is a whole-collection payload, so an omitted
 * set can never be read as a deletion.
 */
const uuid = z.string().uuid();
const isoInstant = z.string().datetime({ offset: true });
const sha256Hex = z.string().regex(/^[0-9a-f]{64}$/);
export const assumptionVersionStateSchema = z.enum([
    'draft',
    'in_review',
    'changes_requested',
    'published',
    'withdrawn',
]);
export const assumptionSetOriginSchema = z.enum(['product', 'firm', 'import', 'legacy']);
/** Product templates a new set can start from (spec §3.4). */
export const productTemplateKeySchema = z.enum(['conservative', 'moderate', 'optimistic']);
/**
 * A set's name: 1–80 characters (spec §3.3), and — because it prints beside the
 * figures on a client's report — barred from advice-like wording by the same
 * rule the 40-character legacy label uses.
 */
export const assumptionSetNameSchema = z
    .string()
    .transform((s) => s.trim().replace(/\s+/g, ' '))
    .pipe(z
    .string()
    .min(1, 'A set needs a name')
    .max(80, 'A set name cannot be longer than 80 characters')
    .refine((s) => !containsAdviceWording(s), {
    message: 'This name appears on client reports, so it cannot contain advice-like words — the tool is a modelling aid, not advice',
}));
/** Case-insensitive uniqueness key, mirrored by `normalised_name` in SQL. */
export function normaliseSetName(name) {
    return name.trim().replace(/\s+/g, ' ').normalize('NFKC').toLowerCase();
}
export const assumptionSetTagsSchema = z
    .array(z.string().trim().min(1).max(40))
    .max(20, 'A set can carry at most 20 tags')
    .refine((t) => new Set(t.map((x) => x.toLowerCase())).size === t.length, {
    message: 'Tags must be distinct',
});
// --- permitted actions ----------------------------------------------------------
/** Server-derived; the UI renders from these, the server re-checks regardless. */
export const assumptionSetActionsSchema = z
    .object({
    editMetadata: z.boolean(),
    createDraft: z.boolean(),
    makeDefault: z.boolean(),
    archive: z.boolean(),
    restore: z.boolean(),
})
    .strict();
export const assumptionVersionActionsSchema = z
    .object({
    edit: z.boolean(),
    validate: z.boolean(),
    submit: z.boolean(),
    review: z.boolean(),
    publish: z.boolean(),
    withdraw: z.boolean(),
})
    .strict();
// --- summaries and detail -------------------------------------------------------
export const assumptionVersionSummarySchema = z
    .object({
    id: uuid,
    setId: uuid,
    state: assumptionVersionStateSchema,
    versionNumber: z.number().int().nullable(),
    draftRevision: z.number().int(),
    publishedName: z.string().nullable(),
    contentHash: sha256Hex.nullable(),
    effectiveAt: isoInstant.nullable(),
    publishedAt: isoInstant.nullable(),
    reviewDueAt: isoInstant.nullable(),
    retiredFromSelectionAt: isoInstant.nullable(),
    updatedAt: isoInstant,
})
    .strict();
export const assumptionSetSummarySchema = z
    .object({
    id: uuid,
    name: z.string(),
    description: z.string(),
    tags: z.array(z.string()),
    origin: assumptionSetOriginSchema,
    legacyKey: z.string().nullable(),
    archived: z.boolean(),
    isDefault: z.boolean(),
    metadataRevision: z.number().int(),
    latestPublished: assumptionVersionSummarySchema.nullable(),
    workingDraft: assumptionVersionSummarySchema.nullable(),
    updatedAt: isoInstant,
    actions: assumptionSetActionsSchema,
})
    .strict();
export const assumptionIssueSchema = z
    .object({ key: z.string(), code: z.string(), message: z.string() })
    .strict();
export const assumptionValidationSchema = z
    .object({
    errors: z.array(assumptionIssueSchema),
    warnings: z.array(assumptionIssueSchema),
    coverage: z.record(z
        .object({
        custom: z.number().int(),
        inherited: z.number().int(),
        missing: z.number().int(),
        invalid: z.number().int(),
    })
        .strict()),
})
    .strict();
export const assumptionVersionDetailSchema = assumptionVersionSummarySchema
    .extend({
    schemaVersion: z.number().int(),
    registryVersion: z.number().int(),
    content: assumptionSetContentSchema,
    /** Content with the base chain flattened in — what a run would read. */
    effectiveValues: z.record(assumptionValueSchema),
    submittedHash: sha256Hex.nullable(),
    actions: assumptionVersionActionsSchema,
})
    .strict();
// --- requests --------------------------------------------------------------------
export const LIBRARY_PAGE_SIZES = [10, 25, 50, 100];
export const assumptionSetListQuerySchema = z
    .object({
    q: z.string().trim().max(120).optional(),
    status: z.enum(['active', 'archived', 'all']).default('active'),
    limit: z.coerce
        .number()
        .int()
        .refine((n) => LIBRARY_PAGE_SIZES.includes(n), {
        message: 'Page size must be 10, 25, 50 or 100',
    })
        .default(25),
    cursor: z.string().max(400).optional(),
})
    .strict();
export const assumptionSetListResponseSchema = z
    .object({
    sets: z.array(assumptionSetSummarySchema),
    nextCursor: z.string().nullable(),
})
    .strict();
export const createAssumptionSetSchema = z
    .object({
    name: assumptionSetNameSchema,
    description: z.string().trim().max(2000).default(''),
    internalPurpose: z.string().trim().max(2000).default(''),
    tags: assumptionSetTagsSchema.default([]),
    source: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('copyVersion'), versionId: uuid }).strict(),
        z.object({ kind: z.literal('productTemplate'), template: productTemplateKeySchema }).strict(),
        z.object({ kind: z.literal('blank') }).strict(),
    ]),
})
    .strict();
export const patchAssumptionSetSchema = z
    .object({
    name: assumptionSetNameSchema.optional(),
    description: z.string().trim().max(2000).optional(),
    internalPurpose: z.string().trim().max(2000).optional(),
    tags: assumptionSetTagsSchema.optional(),
})
    .strict()
    .refine((p) => Object.keys(p).length > 0, { message: 'Nothing to change' });
/** §10.4: at most 500 field mutations in one batch. */
export const MAX_DRAFT_MUTATIONS = 500;
/**
 * One typed change to a draft. `inherit` REMOVES the draft's own value so the
 * base shows through — it is not "set to zero", and there is no mutation that
 * means both.
 */
export const draftMutationSchema = z.discriminatedUnion('op', [
    z.object({ op: z.literal('set'), key: z.string().min(1).max(160), value: assumptionValueSchema }),
    z.object({ op: z.literal('inherit'), key: z.string().min(1).max(160) }),
]);
export const patchAssumptionVersionSchema = z
    .object({
    mutations: z.array(draftMutationSchema).max(MAX_DRAFT_MUTATIONS).default([]),
    evidence: z.array(assumptionEvidenceSchema).max(50).optional(),
    effectiveAt: isoInstant.nullable().optional(),
    reviewDueAt: isoInstant.nullable().optional(),
})
    .strict();
export const publishAssumptionVersionSchema = z
    .object({
    /** Warning codes+keys the publisher has read and accepts. */
    acknowledgedWarnings: z.array(z.string().max(200)).max(500).default([]),
})
    .strict();
export const reviewAssumptionVersionSchema = z
    .object({
    decision: z.enum(['approved', 'changes_requested']),
    /** The exact hash the reviewer looked at; a moved draft is refused. */
    submittedHash: sha256Hex,
    comment: z.string().trim().max(2000).default(''),
    acknowledgedWarnings: z.array(z.string().max(200)).max(500).default([]),
})
    .strict();
export const archiveAssumptionSetSchema = z
    .object({
    /** Required when the set is the active or a scheduled default (AC-09). */
    replacementVersionId: uuid.optional(),
})
    .strict();
// --- default selection -------------------------------------------------------------
export const defaultSelectionSchema = z
    .object({
    /** The selection row itself — what a scheduled change is cancelled by. */
    id: uuid,
    scopeKey: z.string(),
    versionId: uuid,
    setId: uuid,
    setName: z.string(),
    versionNumber: z.number().int(),
    status: z.enum(['scheduled', 'active']),
    effectiveAt: isoInstant,
    revision: z.number().int(),
})
    .strict();
export const defaultSelectionResponseSchema = z
    .object({
    /** null = no firm default chosen; new plans start on the product Base case. */
    active: defaultSelectionSchema.nullable(),
    scheduled: z.array(defaultSelectionSchema),
})
    .strict();
export const putDefaultSelectionSchema = z
    .object({
    versionId: uuid,
    /** Omitted = now. A future instant schedules the change. */
    effectiveAt: isoInstant.optional(),
})
    .strict();
export const assumptionSetResponseSchema = z
    .object({ set: assumptionSetSummarySchema, versions: z.array(assumptionVersionSummarySchema) })
    .strict();
export const assumptionVersionResponseSchema = z
    .object({ version: assumptionVersionDetailSchema, validation: assumptionValidationSchema })
    .strict();
// --- where-used (spec §3.2 row actions, §7.4 archive) ------------------------------
/**
 * How far a published version reaches: the plans pinned to it that the CALLER
 * may see. `visibility` says which of the two that is, because "2 plans" means
 * different things to an owner and to a colleague who holds one client — a
 * count with no scope is a number a reader can only misread.
 *
 * Named clients are the ones the clients LIST would show; the count and the
 * names come from the same query under the same policy, so they cannot
 * disagree.
 */
export const assumptionWhereUsedSchema = z
    .object({
    planCount: z.number().int().min(0),
    clients: z
        .array(z.object({ clientId: z.string().uuid(), displayName: z.string() }).strict())
        .max(200),
    /** True when more plans match than are NAMED above (`planCount` is exact). */
    truncated: z.boolean(),
    visibility: z.enum(['all-visible', 'limited-to-caller']),
})
    .strict();
//# sourceMappingURL=api.js.map